import { useState } from 'react'
import { snakeCase } from 'lodash'

import { getDeviceType, getOperationalSystem, getWindow } from 'services/window'
import { RequestContactSaleMutationVariables, SaleDeviceInfoPayload, SaleTrackingInfoPayload } from 'services/graphql/sdk'
import { sdk } from 'services/graphql'

type SourceInfoPayload = Pick<RequestContactSaleMutationVariables, 'source' | 'sourcePlatform' | 'sourceUrl'>

/** Sales Tracking Data */
const saleTrackingInfoKeys: Array<keyof SaleTrackingInfoPayload> = [
  /** ID do Anúncio em outras plataformas */
  'adId',
  /** ID do Conjunto de Anúncios em outras plataformas */
  'adsetId',
  /** ID da Campanha de Anúncios em outras plataformas */
  'campaignId',
  /** Click-to-WhatsApp Ads click ID */
  'ctwaClid',
  /** Google Display Network click ID */
  'dclid',
  /** Meta click ID */
  'fbclid',
  /** Nome do Site ou Fonte de Origem do Tráfego - Google Ads */
  'gadSource',
  /** Google Ads click ID (iOS 14+) */
  'gbraid',
  /** Google click ID */
  'gclid',
  /** Microsoft Ads / Bing Ads click ID */
  'msclkid',
  /** Posição do Anúncio ou do Click na Busca */
  'placement',
  /** Dados brutos dos parâmetros de rastreamento */
  'rawParams',
  /** Nome do Site ou Fonte de Origem do Tráfego */
  'siteSourceName',
  /** TikTok click ID */
  'ttclid',
  /** Twitter click ID */
  'twclid',
  /** UTM - Campanha */
  'utmCampaign',
  /** UTM - Conteúdo */
  'utmContent',
  /** UTM - Meio de divulgação */
  'utmMedium',
  /** UTM - Fonte de tráfego */
  'utmSource',
  /** UTM - Termo de busca */
  'utmTerm',
  /** Meta click ID (iOS 14+) */
  'wbraid',
  /** Yandex Ads click ID */
  'yclid',
]

/** Sales Tracking Data formatted in Snake Case */
const trackingInfoKeys = saleTrackingInfoKeys.map(snakeCase)

const useRequestContactSale = () => {
  const [loading, setLoading] = useState<boolean>(false)

  const requestContactSale = async (variables: RequestContactSaleMutationVariables) => {
    setLoading(true)
    try {
      const result = await sdk.requestContactSale(variables)
      return result
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Parses the URL's search parameters for any of the tracking parameters known to the API.
   * @returns {SaleTrackingInfoPayload} object filled with the values ​​of known tracking parameters, if any.
   */
  const getTrackingInfoPayload = (): SaleTrackingInfoPayload => {
    const windowObject = getWindow()
    if (!windowObject) return {}

    const urlParams = new URLSearchParams(windowObject.location.search)
    const trackingInfoPayload: SaleTrackingInfoPayload = trackingInfoKeys.reduce(
      (prev, curr) => {
        const paramValue = urlParams.get(curr)
        return !paramValue
          ? prev
          : {
              ...prev,
              [curr]: paramValue,
            }
      },
      { rawParams: JSON.stringify(Object.fromEntries(urlParams.entries())) },
    )

    return trackingInfoPayload
  }

  /**
   * Retrieves metadata from the accessing device.
   * @returns {SaleDeviceInfoPayload} object filled with the metadata of the device used. Unrecovered data is not included in the final object.
   */
  const getDeviceInfoPayload = (): SaleDeviceInfoPayload => {
    const windowObject = getWindow()
    if (!windowObject || !navigator) return {}

    const userAgent = navigator.userAgent
    const referrer = document.referrer || undefined

    const deviceInfoPayload: SaleDeviceInfoPayload = {
      userAgent,
      os: getOperationalSystem(userAgent),
      type: getDeviceType(userAgent),
      ...(referrer ? { referer: referrer } : {}),
    }

    return deviceInfoPayload
  }

  /**
   * Retrieves the source information about the Contact Sale.
   * @returns {SourceInfoPayload} object filled with the metadata of the Sales source. Unrecovered data is not included in the final object.
   */
  const getSourceInfoPayload = (): SourceInfoPayload => {
    const windowObject = getWindow()

    const sourceInfoPayload: SourceInfoPayload = {
      source: 'Site Hubs',
      sourcePlatform: 'Browser',
      ...(windowObject ? { sourceUrl: windowObject.location.pathname } : {}),
    }

    return sourceInfoPayload
  }

  return { getDeviceInfoPayload, requestContactSale, getTrackingInfoPayload, getSourceInfoPayload, loading }
}

export default useRequestContactSale
